/**
 * Multiplication en rectangle — logique pure (Boîte à outils, 6e → 5e).
 *
 * a × b est l'aire d'un rectangle a sur b. On découpe chaque facteur selon
 * ses rangs (23 = 20 + 3, 2,3 = 2 + 0,3) : le rectangle se découpe en
 * morceaux dont les aires sont les produits partiels ; leur somme est le
 * produit. La multiplication posée additionne les mêmes morceaux, ligne par
 * ligne (une ligne par chiffre du second facteur). Décimaux : on multiplie
 * les entiers (23 × 14) puis on place la virgule.
 *
 * Les facteurs sont des entiers de 1 à 99, ou des décimaux à un chiffre après
 * la virgule de 0,1 à 9,9 (stockés en dixièmes).
 */

import { formatNumber } from '../../ui/format';

export type Kind = 'int' | 'dec';

/** décomposition selon les rangs : 23 → [20, 3], 40 → [40], 7 → [7] (en unités ou en dixièmes) */
export function parts(n: number): number[] {
  const out: number[] = [];
  const s = String(n);
  for (let i = 0; i < s.length; i++) {
    const d = Number(s[i]);
    if (d) out.push(d * 10 ** (s.length - 1 - i));
  }
  return out;
}

/** écriture d'un facteur ou d'une part : entier, ou dixièmes → décimal */
export const factorText = (n: number, kind: Kind) =>
  kind === 'int' ? String(n) : formatNumber(n / 10, 1);

const NNBSP = String.fromCharCode(0x202f);

/** écriture d'un produit partiel : entier (10 000 groupé), ou centièmes → décimal */
export const productText = (n: number, kind: Kind) =>
  kind === 'int'
    ? n >= 10000
      ? String(n).replace(/\B(?=(\d{3})+$)/g, NNBSP)
      : String(n)
    : formatNumber(n / 100, 2);

export interface Piece {
  /** indices de colonne (part de a) et de ligne (part de b) */
  col: number;
  row: number;
  a: number;
  b: number;
  /** produit : entier, ou centièmes pour les décimaux */
  p: number;
}

export interface Plan {
  kind: Kind;
  a: number;
  b: number;
  aParts: number[];
  bParts: number[];
  pieces: Piece[];
  /** a × b (unités, ou centièmes) */
  total: number;
}

export function plan(a: number, b: number, kind: Kind): Plan {
  const aParts = parts(a);
  const bParts = parts(b);
  const pieces: Piece[] = [];
  bParts.forEach((pb, row) =>
    aParts.forEach((pa, col) => pieces.push({ col, row, a: pa, b: pb, p: pa * pb })),
  );
  return { kind, a, b, aParts, bParts, pieces, total: a * b };
}

/** « 23 = 20 + 3 » */
export function splitText(n: number, kind: Kind): string {
  const ps = parts(n);
  const t = factorText(n, kind);
  return ps.length > 1
    ? `${t} = ${ps.map((p) => factorText(p, kind)).join(' + ')}`
    : `${t} ne se découpe pas`;
}

/**
 * Distributivité SIMPLE (5e) : un facteur entier, l'autre découpé — c'est ce
 * que fait la multiplication posée. « 23 × (10 + 4) = 23 × 10 + 23 × 4 ».
 * `null` si aucun facteur ne se découpe.
 */
export function distributeText(pl: Plan): string | null {
  const k = pl.kind;
  const at = factorText(pl.a, k);
  const bt = factorText(pl.b, k);
  const sum = (ps: number[]) => `(${ps.map((p) => factorText(p, k)).join(' + ')})`;
  if (pl.bParts.length > 1)
    return `${at} × ${sum(pl.bParts)} = ${pl.bParts.map((p) => `${at} × ${factorText(p, k)}`).join(' + ')}`;
  if (pl.aParts.length > 1)
    return `${sum(pl.aParts)} × ${bt} = ${pl.aParts.map((p) => `${factorText(p, k)} × ${bt}`).join(' + ')}`;
  return null;
}

export const sumText = (pl: Plan) => pl.pieces.map((x) => productText(x.p, pl.kind)).join(' + ');

/** l'entier qu'on écrit pour un facteur (2,3 → 23 ; 2 → 2) et son nombre de décimales */
export function written(n: number, kind: Kind): { int: number; decimals: number } {
  if (kind === 'int' || n % 10 === 0) return { int: kind === 'int' ? n : n / 10, decimals: 0 };
  return { int: n, decimals: 1 };
}

/** un entier écrit avec `d` chiffres après la virgule : (350, 2) → « 3,50 » */
export function withComma(n: number, d: number): string {
  if (d === 0) return String(n);
  const s = String(n).padStart(d + 1, '0');
  return `${s.slice(0, -d)},${s.slice(-d)}`;
}

/** une ligne de la multiplication posée : a × (chiffre × 10ᵏ), sur les ENTIERS écrits */
export interface PosedLine {
  /** ligne du rectangle qui lui correspond (part de b) */
  row: number;
  /** « 23 × 4 » */
  label: string;
  value: number;
}

/**
 * Multiplication posée sur les entiers écrits sans virgule (2,3 × 1,4 :
 * 23 × 14 ; 2 × 1,4 : 2 × 14), une ligne par chiffre non nul du second
 * facteur, des unités vers les dizaines ; `result` avec sa virgule
 * (« 3,50 ») et `decimals` = nombre de chiffres après la virgule.
 */
export function posed(pl: Plan): {
  top: number;
  bottom: number;
  lines: PosedLine[];
  product: number;
  decimals: number;
  result: string;
} {
  const wa = written(pl.a, pl.kind);
  const wb = written(pl.b, pl.kind);
  const lines: PosedLine[] = [];
  const s = String(wb.int);
  for (let k = 0; k < s.length; k++) {
    const d = Number(s[s.length - 1 - k]);
    if (!d) continue;
    const part = d * 10 ** k;
    // part écrite → part du rectangle (en dixièmes si le facteur décimal est entier)
    const inPlan = pl.kind === 'dec' && wb.decimals === 0 ? part * 10 : part;
    lines.push({
      row: pl.bParts.indexOf(inPlan),
      label: `${wa.int} × ${part}`,
      value: wa.int * part,
    });
  }
  const product = wa.int * wb.int;
  const decimals = wa.decimals + wb.decimals;
  return {
    top: wa.int,
    bottom: wb.int,
    lines,
    product,
    decimals,
    result: withComma(product, decimals),
  };
}

/** où placer la virgule, avec le vrai nombre de décimales de chaque facteur */
export function commaText(pl: Plan): string {
  const at = factorText(pl.a, 'dec');
  const bt = factorText(pl.b, 'dec');
  const wa = written(pl.a, 'dec');
  const wb = written(pl.b, 'dec');
  const p = posed(pl);
  const first = wa.decimals ? `${at} a 1 chiffre après la virgule` : `${at} n’a pas de virgule`;
  const second = wb.decimals
    ? `${bt} ${wa.decimals ? 'en a 1 aussi' : 'en a 1'}`
    : `${bt} ${wa.decimals ? 'n’en a pas' : 'non plus'}`;
  const final = productText(pl.total, 'dec');
  const where =
    p.decimals === 0
      ? `aucun chiffre après la virgule : ${at} × ${bt} = ${final}.`
      : `le produit a ${wa.decimals} + ${wb.decimals} = ${p.decimals} chiffre${p.decimals > 1 ? 's' : ''} après la virgule : ${at} × ${bt} = ${p.result}${p.result !== final ? ` = ${final}` : ''}.`;
  return `On calcule ${wa.int} × ${wb.int} = ${p.product} sans les virgules. ${first}, ${second} : ${where}`;
}
/** ordre de grandeur : un seul chiffre significatif (23 → 20 ; en dixièmes, 2,3 → 2) */
export function estimate(n: number): number {
  const p = 10 ** (String(n).length - 1);
  return Math.round(n / p) * p;
}

/** `null` quand l'ordre de grandeur serait le calcul lui-même (7 × 8) */
export function estimateText(pl: Plan): string | null {
  const ea = estimate(pl.a);
  const eb = estimate(pl.b);
  if (ea === pl.a && eb === pl.b) return null;
  return `Ordre de grandeur : ${factorText(ea, pl.kind)} × ${factorText(eb, pl.kind)} = ${productText(ea * eb, pl.kind)}.`;
}
